import { toWorker } from '@/modules/Editor/utils/workers/toWorker'
import type { TipShape } from '@/modules/Editor/plugins/brush/consts'
import type { IPoint } from '@/modules/Editor/point'
import { Actions } from './types'
import Worker from './worker?worker'

export interface StrokeData {
  imagePoint: IPoint
  width: number
  tipShape: TipShape
}

export interface BrushProperties {
  isEraser: boolean
  eraseAll: boolean
  emptyPixelsOnly: boolean
}

interface WorkerResponse {
  bounds: {
    minX: number
    maxX: number
    minY: number
    maxY: number
  }
  labelsBeingOverwritten: number[]
  buffer?: Uint8Array // Optional buffer for non-SharedArrayBuffer support
}

export class BrushWorker {
  private worker: Worker | null = null
  private toWorker: ReturnType<typeof toWorker> | null = null

  constructor() {
    this.worker = new Worker()
    this.toWorker = toWorker(this.worker)
  }

  init(
    buffer: SharedArrayBuffer | ArrayBuffer,
    width: number,
    height: number,
    labelIndex: number,
    properties: BrushProperties,
    pixelMask?: Uint8Array,
  ): Promise<void> {
    if (!this.toWorker) {
      return Promise.reject()
    }

    return this.toWorker.postMessage(Actions.INIT, {
      buffer,
      width,
      height,
      labelIndex,
      properties,
      pixelMask,
    })
  }

  updateProperties(properties: Partial<BrushProperties>, pixelMask?: Uint8Array): Promise<void> {
    if (!this.toWorker) {
      return Promise.reject()
    }

    return this.toWorker.postMessage(Actions.UPDATE_PROPERTIES, { properties, pixelMask })
  }

  stroke(strokeData: StrokeData): Promise<void> {
    if (!this.toWorker) {
      return Promise.reject()
    }

    return this.toWorker.postMessage(Actions.STROKE, { strokeData })
  }

  finalize(): Promise<WorkerResponse | undefined> {
    if (!this.toWorker) {
      return Promise.reject()
    }

    return this.toWorker.postMessage<WorkerResponse>(Actions.FINALIZE)
  }

  clear(): Promise<void> {
    if (!this.toWorker) {
      return Promise.resolve()
    }

    return this.toWorker.postMessage(Actions.CLEAR)
  }

  updateLabel(
    buffer: SharedArrayBuffer | ArrayBuffer,
    width: number,
    height: number,
    labelIndex: number,
    updatedLabelIndex: number,
    bounds: {
      xMin: number
      xMax: number
      yMin: number
      yMax: number
    },
  ): Promise<WorkerResponse | undefined> {
    if (!this.toWorker) {
      return Promise.reject()
    }

    return this.toWorker.postMessage<WorkerResponse>(Actions.UPDATE_LABEL, {
      buffer,
      width,
      height,
      labelIndex,
      updatedLabelIndex,
      bounds,
    })
  }

  cleanup(): void {
    this.toWorker?.cleanup()
    this.toWorker = null
    this.worker?.terminate()
    this.worker = null
  }
}
